import type {
  LoadSubsetOptions,
  SyncConfig,
  SyncConfigRes,
} from "@tanstack/db";

/**
 * Hold on-demand subset unloads until every in-flight subset load has settled.
 *
 * Why: a query-backed `on-demand` collection tracks which subset query owns
 * each row and deletes a row the moment its last owner unloads. When a live
 * query's predicate changes (a token stack gains one more asset id), react-db
 * starts the replacement subset during render and garbage-collects the old
 * live query about a millisecond later. The old subset therefore unloads while
 * the replacement's fetch is still in flight, so every row the two subsets
 * share is deleted and re-inserted a network round-trip later. On screen that
 * is a resolved token icon collapsing to its symbol fallback and back.
 *
 * Deferring the unload lets the replacement subset take ownership first, so
 * shared rows never leave the collection. Rows only the retired subset owned
 * are still deleted, just once the loads settle. Failed loads settle too, so a
 * failed replacement never pins the retired subset forever.
 */
export function deferSubsetUnloadsUntilLoadsSettle<
  T extends object,
  TKey extends string | number,
  TConfig extends { sync: SyncConfig<T, TKey> },
>(config: TConfig & { sync: SyncConfig<T, TKey> }): TConfig {
  const innerSync = config.sync.sync;
  const sync: SyncConfig<T, TKey>["sync"] = (params) => {
    const result = innerSync(params);
    // A bare cleanup function means the adapter has no subset handlers.
    return result && typeof result !== "function"
      ? deferUnloadsUntilLoadsSettle(result)
      : result;
  };

  return { ...config, sync: { ...config.sync, sync } };
}

export function deferUnloadsUntilLoadsSettle(
  result: SyncConfigRes,
): SyncConfigRes {
  const { loadSubset, unloadSubset } = result;
  if (!loadSubset || !unloadSubset) {
    return result;
  }

  const inFlightLoads = new Set<Promise<void>>();
  const heldUnloads: LoadSubsetOptions[] = [];
  let draining = false;

  const drainHeldUnloads = async (): Promise<void> => {
    if (draining) {
      return;
    }
    draining = true;
    try {
      // A load can start while we wait, so re-check until nothing is pending.
      while (inFlightLoads.size > 0) {
        await Promise.allSettled(inFlightLoads);
      }
    } finally {
      draining = false;
    }
    for (const options of heldUnloads.splice(0)) {
      unloadSubset(options);
    }
  };

  return {
    ...result,
    loadSubset: (options) => {
      const loaded = loadSubset(options);
      if (loaded instanceof Promise) {
        inFlightLoads.add(loaded);
        const settle = () => {
          inFlightLoads.delete(loaded);
        };
        void loaded.then(settle, settle);
      }
      return loaded;
    },
    unloadSubset: (options) => {
      if (inFlightLoads.size === 0) {
        unloadSubset(options);
        return;
      }
      heldUnloads.push(options);
      void drainHeldUnloads();
    },
  };
}
